/**
 * El motor del tutorial: dónde va cada quien y cuándo se da por hecho un paso.
 *
 * The hard part of a tutorial is not drawing the arrow. It is knowing when to move on, and
 * every cheap way of deciding that is wrong. Advancing on a tap teaches the tap, not the task —
 * somebody who presses the right button and then abandons the form has learned nothing, and
 * the tutorial has cheerfully told them they succeeded. Advancing on a timer is worse.
 *
 * So a step is finished when the shop has actually changed: a till exists, a sale exists, a
 * product has a price. That is read from the same projections the screens read, which means a
 * step passes if the person did the task — by the route the lesson showed them, or by any
 * other route they found, or last week before the lesson started. It cannot be faked by
 * pressing things, and it cannot be failed by doing it correctly the wrong way.
 *
 * Three kinds of step, in descending order of how much they prove:
 *
 *   `cuando`     the shop changed. The real thing.
 *   `apareceEl`  something is now on screen — a cart line, an open payment panel. For the
 *                middle of a task, where nothing has been committed yet and there is
 *                genuinely nothing in the database to look at.
 *   `alTocar`    they pressed the thing. Only for navigation, where pressing IS the task.
 *
 * A step with none of those is an explanation, and it waits for "Entendido".
 */

import { esDeudaAnterior } from '@abastece/core';
import { db } from '../db/dexie.js';
import { sesionAbierta } from '../lib/caja.js';

// ---------------------------------------------------------------------------
// Lo que el motor sabe de la tienda
// ---------------------------------------------------------------------------

/**
 * Everything any lesson is allowed to ask about, gathered in one pass.
 *
 * Deliberately a flat bag of counts rather than arbitrary queries per step. A lesson is data,
 * and data that can run a database query is code — it would be able to be slow, to throw, and
 * to see a half-written transaction. Counting a fixed set of things every so often cannot.
 */
export interface Instantanea {
  productos: number;
  conPrecio: number;
  /** Every price added up: it moves when any price is set or changed. */
  sumaPrecios: number;
  proveedores: number;
  compras: number;
  ventas: number;
  ventasAnuladas: number;
  /** Sales with some of it on fiado. */
  ventasFiadas: number;
  abonos: number;
  cajaAbierta: boolean;
  cortes: number;
  clientes: number;
  pedidos: number;
  gastos: number;
  conteos: number;
  traslados: number;
  planillas: number;
}

export const INSTANTANEA_VACIA: Instantanea = {
  productos: 0,
  conPrecio: 0,
  sumaPrecios: 0,
  proveedores: 0,
  compras: 0,
  ventas: 0,
  ventasAnuladas: 0,
  ventasFiadas: 0,
  abonos: 0,
  cajaAbierta: false,
  cortes: 0,
  clientes: 0,
  pedidos: 0,
  gastos: 0,
  conteos: 0,
  traslados: 0,
  planillas: 0,
};

export async function tomarInstantanea(): Promise<Instantanea> {
  const [
    productos,
    precios,
    proveedores,
    compras,
    ventas,
    caja,
    sesiones,
    clientes,
    pedidos,
    gastos,
    conteos,
    traslados,
    planillas,
    abonos,
  ] = await Promise.all([
    db.productos.count(),
    db.precios.toArray(),
    db.proveedores.count(),
    // Goods received: a debt from before is not the lesson's purchase.
    db.compras.filter((c) => !esDeudaAnterior(c)).count(),
    db.ventas.toArray(),
    sesionAbierta(),
    db.sesiones.toArray(),
    db.clientes.count(),
    db.pedidos.count(),
    db.gastos.count(),
    db.conteos.count(),
    db.traslados.count(),
    db.planillas.count(),
    db.abonos.count(),
  ]);

  return {
    productos,
    // Sale units, not products — but a shop with any priced unit has got the idea, and the
    // lesson only needs to know whether the person has done it once.
    conPrecio: precios.filter((p) => p.precio > 0).length,
    sumaPrecios: precios.reduce((a, p) => a + p.precio, 0),
    proveedores,
    compras,
    ventas: ventas.length,
    ventasAnuladas: ventas.filter((v) => v.anulada).length,
    ventasFiadas: ventas.filter((v) => v.pagos.some((p) => p.forma === 'credito')).length,
    abonos,
    cajaAbierta: Boolean(caja),
    cortes: sesiones.filter((s) => s.estado === 'cerrada').length,
    clientes,
    pedidos,
    gastos,
    conteos,
    traslados,
    planillas,
  };
}

// ---------------------------------------------------------------------------
// Progreso
// ---------------------------------------------------------------------------

const CLAVE_HECHAS = 'abastece.tutorial.hechas';
const CLAVE_VISTO = 'abastece.tutorial.visto';

/** Lessons finished, by id. Kept per device: the phone teaches whoever is holding it. */
export function leccionesHechas(): Set<string> {
  try {
    const crudo = localStorage.getItem(CLAVE_HECHAS);
    return new Set(crudo ? (JSON.parse(crudo) as string[]) : []);
  } catch {
    return new Set();
  }
}

export function marcarHecha(id: string): void {
  try {
    const hechas = leccionesHechas();
    hechas.add(id);
    localStorage.setItem(CLAVE_HECHAS, JSON.stringify([...hechas]));
  } catch {
    /* Progress is a convenience. Losing it repeats a lesson; it does not break one. */
  }
}

export function olvidarProgreso(): void {
  try {
    localStorage.removeItem(CLAVE_HECHAS);
    localStorage.removeItem(CLAVE_VISTO);
  } catch {
    /* Nothing to forget. */
  }
}

/**
 * Whether this device has ever offered the tutorial.
 *
 * Separate from "finished every lesson" on purpose: the offer is made once, and somebody who
 * declines it is not asked again on every reload. They can still start it from the menu.
 */
export const yaSeOfrecio = (): boolean => {
  try {
    return localStorage.getItem(CLAVE_VISTO) === '1';
  } catch {
    return true;
  }
};

export const marcarOfrecido = (): void => {
  try {
    localStorage.setItem(CLAVE_VISTO, '1');
  } catch {
    /* Then it offers again next time, which is the harmless direction. */
  }
};
